import type { SigningTranslations } from "../../types/signing";

export const signing: SigningTranslations = {
  sidebar: "Signing",
  title: "Signing",
  subtitle: "Signed containers and signing chains for this project",
  tabs: { mine: "My signatures", all: "All signatures" },
  search: "Search documents…",
  bulk: {
    downloadSkipped: "Skipped {count} selected document(s) because their files are unavailable.",
  },
  filter: {
    label: "Filter",
    status: "Status",
    signedBy: "Signed by",
    date: "Date",
    from: "From",
    to: "To",
    clear: "Clear",
    noSigners: "No one has signed yet.",
  },
  columns: {
    document: "Document",
    sender: "Sender",
    created: "Created",
    createdTooltip:
      "When this was created in Franklin: the upload date of a signed container, or when a signing was started. Not a date from the file's metadata.",
    status: "Status",
  },
  status: {
    awaitingYou: "Waiting for you",
    awaitingOthers: "Waiting for others",
    finalizing: "Finalising",
    signed: "Signed",
    failed: "Failed",
    cancelled: "Cancelled",
    expired: "Expired",
    unknown: "Unknown",
  },
  chain: {
    signers: "Signers",
    empty: "No signers recorded yet.",
    step: "Step {position}",
    you: "You",
    progress: "{signed} of {total} signed",
    inOrder: "Signing in order",
    signerStatus: {
      pending: "Waiting",
      signed: "Signed",
      rejected: "Declined",
      removed: "Removed",
    },
  },
  method: {
    smartId: "Smart-ID",
    mobileId: "Mobile-ID",
    idCard: "ID-card",
    unknown: "Unknown method",
  },
  panel: {
    failedTitle: "This signing did not complete",
    failedDetail: "Reported by the signing provider:",
    eyebrow: "Signature details",
    signatures: "Signatures",
    containerContents: "Container contents",
    documentsToSign: "Documents in this session",
    contentsLoadFailed: "Could not load the file list. Please try again.",
    noFiles: "No files recorded.",
    signedOn: "Signed",
    startedOn: "Started",
    download: "Download",
    downloadFailed: "Could not prepare the download. Please try again.",
    checkStatus: "Check status",
    expiresOn: "Expires {date}",
    waitingForOthers: "Waiting for the signers above. You'll be emailed when it is your turn.",
    finalizing: "Every signature is in. Sealing the signed container — this takes a moment.",
    finalizingAfterDecline:
      "A signer declined, so nobody is left to sign. Checking with the signing provider where this signing landed.",
    signNow: "Sign now",
    signTurnTitle: "Sign documents",
    signTurnHint: "Choose Smart-ID, Mobile-ID or ID-card and complete signing below.",
    signTurnDone: "Your signature is recorded. The document moves on to the next signer.",
    signTurnCompleted: "Signed. The signed container is filed with your documents.",
    signTurnSealing:
      "Your signature is recorded. The signed container is still being sealed — it will appear with your documents once it is ready. You can close this window.",
    signTurnSealingAfterDecline:
      "Your signature is recorded. A signer declined, so nobody is left to sign. Where this signing landed will show on the Signing page — you can close this window.",
    signTurnClosedAfterSigning:
      "Your signature is recorded. This signing has since been closed — it was cancelled or has expired — so no signed container will be filed.",
    signTurnFailed: "Could not open this signing.",
    signTurnSignFailed:
      "Signing did not go through. Try again below, or close this window and open it again.",
    cancelSigning: "Cancel signing",
    cancelConfirmTitle: "Cancel this signing?",
    cancelConfirmBody:
      "Everyone still waiting loses access and no container is produced. Signatures already given cannot be reused.",
    cancelConfirmAction: "Cancel signing",
    cancelKeep: "Keep it open",
    cancelled: "Signing cancelled.",
    signerUnknown: "Signer name not recorded",
  },
  refusal: {
    unknownLink: "This signing is no longer available to you.",
    chainClosed: "This signing is closed — it was completed, cancelled or has expired.",
    alreadySigned: "You have already signed this.",
    notYourTurn: "It is not your turn yet. You'll be emailed when it is.",
    noLongerAMember: "You are no longer a member of the organisation that started this signing.",
    chainDiffers: "A different signing for these documents is already in progress.",
    finalizing: "This signing is being finalised — it can no longer be cancelled.",
    fullySigned: "Everyone has already signed — this signing can no longer be cancelled.",
    notAllowed: "You are not allowed to do this.",
    alreadyInProgress:
      "A signing for these documents is already in progress. Refresh the page and try again.",
    requestRejected: "This signing request was rejected. Check the details and try again.",
  },
  selection: {
    selectAll: "Select all signed containers",
    selectRow: "Select {title}",
  },
  unfiled: {
    badge: "No project",
    hint: "This signing isn't filed under a project, so it is listed in every project of this building.",
  },
  empty: {
    all: {
      title: "Nothing signed yet",
      subtitle: "Signed containers appear here once documents have been signed in this project.",
    },
    mine: {
      title: "Nothing here is yours yet",
      subtitle: "Signings you started, and the ones you were asked to sign, appear here.",
    },
    filtered: {
      title: "No matches",
      subtitle: "No signatures match the current search or filters.",
    },
  },
  loadFailed: "Could not load signatures.",
  truncated: "Showing only the {count} most recent items.",
  signatureCount: { one: "{count} signature", many: "{count} signatures" },
  dialog: {
    title: "Sign documents",
    containerName: "Signed file name",
    output: {
      label: "Output format",
      asice: "ASiC-E container (.asice)",
      pdf: "Signed PDF / PAdES (.pdf)",
      asiceHint: "Keep one or more files together in a signed container.",
      pdfHint: "Keep the document as a PDF with embedded digital signatures.",
      singlePdfRequired:
        "PAdES requires exactly one PDF. Use ASiC-E for multiple files or other file types.",
    },
    documents: "Documents",
    total: "Total",
    sign: "Sign",
    send: "Send for signing",
    cancel: "Cancel",
    close: "Close",
    starting: "Preparing your documents…",
    signingHint: "Choose Smart-ID, Mobile-ID, or ID-card and complete signing below.",
    resumedHint: "Continuing the signing you already started for these documents.",
    completedMsg: "The signed file has been added to your documents.",
    errorTitle: "Signing failed",
    tryAgain: "Try again",
    creatorUnknown:
      "We lost track of your account, so this cannot be sent to other signers. Reload the page and try again.",
    target: {
      mixedTitle: "These documents are in different projects",
      mixedMessage:
        "A signing belongs to one project, so these cannot be signed together. Choose documents from a single project and start again.",
      failedTitle: "Could not prepare this signing",
      failedMessage:
        "We could not check which project these documents are filed in. Please try again.",
    },
    signers: {
      modeLabel: "Signing method",
      selfTitle: "Sign myself",
      selfDescription: "Sign now with ID-card, Mobile-ID, or Smart-ID.",
      sendTitle: "Send to others",
      sendDescription: "Invite people to sign; you sign first.",
      order: "Signing order",
      you: "You",
      addOthers: "Send to others to sign instead…",
      youSignFirst: "You sign first, then it goes to the people below in order.",
      label: "Signers",
      hint: "Leave empty to sign these documents yourself, right now. Add people to send it around instead — they sign step by step, in the order below.",
      placeholder: "Add signers…",
      search: "Search members…",
      none: "No project members found.",
      loading: "Loading members…",
      needProject:
        "Open a project to invite other signers. Without one you can only sign these documents yourself.",
      max: "You sign first, so you can add at most {count} other signers.",
      parallel: "Sign in any order",
      moveUp: "Move this step earlier",
      moveDown: "Move this step later",
      mergeUp: "Sign together with the step above",
      split: "Give this signer a step of their own",
      remove: "Remove this signer",
      youSignAt: "You sign at step {position}.",
      youDoNotSign: "You are not a signer — you are sending this for others to sign.",
    },
    invited: {
      title: "Invitations sent",
      message: "{count} signers were invited. They are emailed as their turn comes.",
      firstUp: "First up: {names}",
    },
    handedOn: {
      title: "Your signature is recorded",
      message:
        "{count} more still to sign. They are emailed as their turn comes, and you'll be emailed once the signed container is ready.",
      finalizing: "Everyone has signed. The signed container is being prepared.",
    },
    cancelOther: "Cancel the other signing and continue",
    cancelling: "Cancelling…",
  },
};
